import React, { useState, useEffect } from "react";
import axios from "axios";
import { 
  Users, MousePointer, MessageSquare, Mail, Phone, 
  Trash2, CheckCircle2, AlertCircle, LogOut, Loader2, ArrowLeft, RefreshCw 
} from "lucide-react";
import { toast } from "@/components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

// Ensure cookies are sent with all requests
axios.defaults.withCredentials = true;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminDashboard() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState("");

  const [enquiries, setEnquiries] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(false);

  // Check if session is already active
  const checkSession = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      if (response.data && response.data.email) {
        setIsAuthenticated(true);
        fetchDashboardData();
      }
    } catch (err) {
      setIsAuthenticated(false);
    } finally {
      setIsCheckingAuth(false);
    }
  };

  useEffect(() => {
    checkSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    if (!loginEmail || !loginPassword) {
      setLoginError("Please enter both email and password.");
      return;
    }
    setIsLoggingIn(true);
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: loginEmail,
        password: loginPassword
      });
      if (response.data && response.data.email) {
        toast.success(`Welcome back, ${response.data.name}!`);
        setIsAuthenticated(true);
        fetchDashboardData();
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setLoginError(formatApiErrorDetail(detail) || err.message);
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
      toast.success("Logged out successfully.");
      setIsAuthenticated(false);
      setEnquiries([]);
      setAnalytics(null);
      setNotifications([]);
    } catch (err) {
      toast.error("Logout failed.");
    }
  };

  const fetchDashboardData = async () => {
    setIsLoadingData(true);
    try {
      const [enqRes, statsRes, notifRes] = await Promise.all([
        axios.get(`${API}/enquiries`),
        axios.get(`${API}/analytics/stats`),
        axios.get(`${API}/notifications/logs`)
      ]);
      setEnquiries(enqRes.data);
      setAnalytics(statsRes.data);
      setNotifications(notifRes.data);
    } catch (err) {
      toast.error("Failed to load dashboard statistics.");
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleStatusChange = async (enquiryId, newStatus) => {
    try {
      await axios.patch(`${API}/enquiries/${enquiryId}`, { status: newStatus });
      toast.success(`Status updated to ${newStatus}`);
      fetchDashboardData();
    } catch (err) {
      toast.error("Failed to update status.");
    }
  };

  const handleDeleteEnquiry = async (enquiryId) => {
    if (!window.confirm("Are you sure you want to permanently delete this enquiry?")) {
      return;
    }
    try {
      await axios.delete(`${API}/enquiries/${enquiryId}`);
      toast.success("Enquiry deleted successfully.");
      fetchDashboardData();
    } catch (err) {
      toast.error("Failed to delete enquiry.");
    }
  };

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex flex-col items-center justify-center gap-3">
        <Loader2 className="animate-spin text-[#D4AF37] h-10 w-10" />
        <p className="text-sm font-medium text-[#0A1E3F]">Securing your connection...</p>
      </div>
    );
  }

  // LOGIN PAGE
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0A1E3F] to-[#162F56] flex flex-col justify-center px-6 py-12 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <a href="/" className="flex flex-col items-center gap-2 text-center">
            <span className="font-display text-3xl font-bold text-white tracking-tight">Jijivisha Consulting</span>
            <span className="text-xs uppercase tracking-widest text-[#D4AF37] font-semibold">Secure Staff Portal</span>
          </a>
          <h2 className="mt-6 text-center text-xl font-display font-medium text-gray-300">
            Sign in to your dashboard
          </h2>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-10 px-8 shadow-2xl rounded-3xl border border-white/10 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-[#D4AF37]"></div>
            
            <form onSubmit={handleLogin} className="space-y-6">
              {loginError && (
                <div className="bg-red-50 border border-red-200 text-red-600 rounded-xl p-4 text-xs flex items-start gap-2 animate-pulse">
                  <AlertCircle size={16} className="shrink-0 mt-0.5" />
                  <span>{loginError}</span>
                </div>
              )}

              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase">
                  Staff Email Address
                </label>
                <input
                  type="email"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  placeholder="admin@jijivisha.com"
                  className="mt-1 block w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none transition-all"
                  required
                  data-testid="admin-email-input"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase">
                  Password
                </label>
                <input
                  type="password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  placeholder="••••••••"
                  className="mt-1 block w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none transition-all"
                  required
                  data-testid="admin-password-input"
                />
              </div>

              <button
                type="submit"
                disabled={isLoggingIn}
                className="w-full bg-[#0A1E3F] hover:bg-[#162F56] text-white py-4 rounded-xl font-semibold uppercase tracking-wider text-sm transition-all shadow-md flex items-center justify-center gap-2"
                data-testid="admin-login-submit-button"
              >
                {isLoggingIn ? (
                  <>
                    <Loader2 className="animate-spin h-5 w-5" />
                    Signing in...
                  </>
                ) : (
                  "Secure Sign In"
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <a href="/" className="inline-flex items-center gap-2 text-xs text-[#0A1E3F] hover:underline font-medium">
                <ArrowLeft size={14} /> Back to Public Website
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // AUTHENTICATED PANEL
  return (
    <div className="min-h-screen bg-[#F8F9FA] text-[#111827]">
      
      {/* Header */}
      <header className="bg-white border-b border-gray-200/80 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" className="font-display text-xl font-bold tracking-tight text-[#0A1E3F]">
              Jijivisha Staff Control Panel
            </a>
            <span className="hidden sm:inline px-2.5 py-0.5 bg-[#D4AF37]/10 text-[#0A1E3F] border border-[#D4AF37]/30 text-[10px] uppercase font-bold tracking-wider rounded-full">
              System Admin
            </span>
          </div>

          <div className="flex items-center gap-4">
            <button 
              onClick={fetchDashboardData} 
              className="p-2.5 text-gray-500 hover:text-[#0A1E3F] bg-gray-50 hover:bg-gray-100 rounded-full transition-colors"
              title="Refresh Data"
            >
              <RefreshCw size={18} className={isLoadingData ? "animate-spin" : ""} />
            </button>
            <button 
              onClick={handleLogout}
              className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-full text-xs font-semibold transition-all border border-red-200/50"
              data-testid="admin-logout-button"
            >
              <LogOut size={14} />
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        
        {/* STATS PANEL */}
        <section data-testid="admin-analytics-stats">
          <h2 className="text-xl font-display font-bold text-[#0A1E3F] mb-4">Operations Overview</h2>
          <div className="grid md:grid-cols-4 gap-6">
            
            <div className="bg-white p-6 rounded-2xl border border-gray-200/50 shadow-sm space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase text-gray-400 font-bold tracking-widest">Enquiries</span>
                <MessageSquare className="text-[#D4AF37]" size={20} />
              </div>
              <p className="text-3xl font-semibold font-display text-[#0A1E3F]">
                {analytics?.total_enquiries ?? 0}
              </p>
              <p className="text-[10px] text-gray-500">Submitted through contact form</p>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-gray-200/50 shadow-sm space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase text-gray-400 font-bold tracking-widest">Total Clicks</span>
                <MousePointer className="text-[#D4AF37]" size={20} />
              </div>
              <p className="text-3xl font-semibold font-display text-[#0A1E3F]">
                {analytics?.total_clicks ?? 0}
              </p>
              <p className="text-[10px] text-gray-500">CTA interactions recorded</p>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-gray-200/50 shadow-sm space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase text-gray-400 font-bold tracking-widest">WhatsApp Leads</span>
                <Phone className="text-[#D4AF37]" size={20} />
              </div>
              <p className="text-3xl font-semibold font-display text-[#0A1E3F]">
                {(analytics?.clicks_breakdown?.whatsapp_hero ?? 0) + 
                 (analytics?.clicks_breakdown?.whatsapp_booking ?? 0) + 
                 (analytics?.clicks_breakdown?.whatsapp_floating ?? 0)}
              </p>
              <p className="text-[10px] text-gray-500">Clicks to open WhatsApp chat</p>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-gray-200/50 shadow-sm space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase text-gray-400 font-bold tracking-widest">Direct Phone Calls</span>
                <Phone className="text-[#0A1E3F]" size={20} />
              </div>
              <p className="text-3xl font-semibold font-display text-[#0A1E3F]">
                {analytics?.clicks_breakdown?.call_now ?? 0}
              </p>
              <p className="text-[10px] text-gray-500">Clicks on &apos;Call Now&apos; phone numbers</p>
            </div>

          </div>
        </section>

        {/* ENQUIRIES TABLE */}
        <section className="bg-white rounded-3xl border border-gray-200/50 shadow-lg overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-lg font-display font-semibold text-[#0A1E3F]">
              Client Consultation Enquiries
            </h2>
            <span className="px-2.5 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
              {enquiries.length} submissions
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-sm" data-testid="admin-enquiries-table">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100 text-xs text-gray-500 uppercase tracking-wider font-semibold">
                  <th className="px-6 py-4">Client Detail</th>
                  <th className="px-6 py-4">Message</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-xs">
                {enquiries.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-12 text-center text-gray-500">
                      No active client enquiries found. Submissions will appear here in real time.
                    </td>
                  </tr>
                ) : (
                  enquiries.map((enq) => (
                    <tr key={enq.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-6 py-4 space-y-1">
                        <div className="font-semibold text-sm text-[#0A1E3F]">{enq.name}</div>
                        <div className="text-gray-500 flex items-center gap-1.5">
                          <Phone size={12} /> {enq.phone}
                        </div>
                        <div className="text-gray-500 flex items-center gap-1.5">
                          <Mail size={12} /> {enq.email}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-600 max-w-sm whitespace-normal break-words">
                        {enq.message || <em className="text-gray-400">No message provided</em>}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider ${
                          enq.status === "new" ? "bg-blue-50 text-blue-600 border border-blue-100" :
                          enq.status === "contacted" ? "bg-amber-50 text-amber-600 border border-amber-100" :
                          "bg-emerald-50 text-emerald-600 border border-emerald-100"
                        }`}>
                          {enq.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right space-x-2">
                        {enq.status !== "contacted" && (
                          <button
                            onClick={() => handleStatusChange(enq.id, "contacted")}
                            className="p-1.5 bg-amber-50 hover:bg-amber-100 text-amber-700 rounded-lg transition-colors border border-amber-200"
                            title="Mark Contacted"
                            data-testid="admin-enquiry-status-contacted"
                          >
                            <CheckCircle2 size={14} />
                          </button>
                        )}
                        {enq.status !== "resolved" && (
                          <button
                            onClick={() => handleStatusChange(enq.id, "resolved")}
                            className="p-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-lg transition-colors border border-emerald-200"
                            title="Mark Resolved"
                            data-testid="admin-enquiry-status-resolved"
                          >
                            <CheckCircle2 size={14} />
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteEnquiry(enq.id)}
                          className="p-1.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition-colors border border-red-200"
                          title="Delete permanently"
                          data-testid="admin-enquiry-delete-button"
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* SIMULATED NOTIFICATION LOGS PANEL */}
        <section className="bg-white rounded-3xl border border-gray-200/50 shadow-lg overflow-hidden" data-testid="admin-notification-logs">
          <div className="px-6 py-5 border-b border-gray-100">
            <h2 className="text-lg font-display font-semibold text-[#0A1E3F]">
              Simulated Dispatch System (SMS & Email Logs)
            </h2>
            <p className="text-[10px] text-gray-400 mt-0.5">Auto-simulates alerts dispatched to staff and customers on client enquiry events</p>
          </div>

          <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-12 text-center text-gray-400 text-xs">
                No simulated dispatches in system yet. Form submissions will trigger log generation here.
              </div>
            ) : (
              notifications.map((notif) => (
                <div key={notif.id} className="p-4 hover:bg-gray-50/50 transition-colors flex justify-between items-start text-xs gap-4">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wider ${
                        notif.type === "email" ? "bg-[#0A1E3F]/10 text-[#0A1E3F]" : "bg-[#D4AF37]/10 text-[#D4AF37]"
                      }`}>
                        {notif.type}
                      </span>
                      <span className="font-semibold text-gray-500">Recipient:</span>
                      <span className="text-[#0A1E3F] font-mono">{notif.recipient}</span>
                    </div>
                    <p className="text-gray-600 font-light leading-relaxed">{notif.message}</p>
                  </div>

                  <div className="text-right space-y-1 shrink-0">
                    <span className="px-2 py-0.5 bg-emerald-50 text-emerald-600 rounded-full text-[9px] font-medium uppercase">
                      {notif.status}
                    </span>
                    <p className="text-[10px] text-gray-400">
                      {new Date(notif.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </section>

      </main>
    </div>
  );
}
